import { tokenAbi } from "@/constants/abi";
import mcAbi from "@/constants/abi/mcAbi";
import { DOGE_TOKENS, SHIB_TOKENS } from "@/constants/project-specific/tokens";
import { useEffect, useState } from "react";
import { type Address, formatEther } from "viem";
import { useReadContracts } from "wagmi";
// import { useGlobalState } from "~~/services/store/store";

const chewyPoolPID = 3;
const masterChefAddress: Address = "0x4c00f75F179F4A8208BC2ba3958Eb8d1C7986418";
const masterChefs = {
  doge: {
    address: "0xdDC5b34c49E2Df3F78cA1B3D3BD9699a4e488c1D" as Address,
    pools: [{
      pid: 25,
      masterChefSlug: "dogeshrek",
      lpAddress: "0xc86e836292e87407060F9bf197E841F62c32fB06" as Address,
      tokenA: DOGE_TOKENS.chewyswap,
      quoteToken: DOGE_TOKENS.wdoge
    }, {
      pid: 26,
      masterChefSlug: "dogeshrek",
      lpAddress: "0x95b0186941121cAd00b226E83293aF5e385661e3" as Address,
      tokenA: DOGE_TOKENS.chewyswap,
      tokenB: DOGE_TOKENS.dc,
      quoteToken: DOGE_TOKENS.dc
    }]
  },
  shib: {
    address: "0x4c00f75F179F4A8208BC2ba3958Eb8d1C7986418" as Address,
    pools: [{
      pid: 25,
      masterChefSlug: "dogeshrek",
      lpAddress: "0xc86e836292e87407060F9bf197E841F62c32fB06" as Address,
      tokenA: DOGE_TOKENS.chewyswap,
      quoteToken: DOGE_TOKENS.wdoge
    }, {
      pid: 2,
      masterChefSlug: "chewyswap",
      lpAddress: "0x324EEf33AF720cE44DEAB7e32F4367a82b4eA43b" as Address,
      tokenA: SHIB_TOKENS.chewyswap,
      tokenB: SHIB_TOKENS.wbone,
      quoteToken: SHIB_TOKENS.wbone
    }]
  }
};
const minChewyPremium = 6900;
interface ChewyBalances {
  shibarium: number;
  doge: number;
  shibariumLp: number;
  dogeLp: number;
  total: number;
}
export function useIsPremium(address?: Address) {
  const [isPremium, setIsPremium] = useState(false);
  const [premPercentage, setPremPercentage] = useState(0);
  const [balance, setBalance] = useState<ChewyBalances | null>(null);
  const {
    data: balanceResults,
    isError: balanceError,
    isLoading: balanceLoading
  } = useReadContracts({
    contracts: [{
      address: masterChefs.shib.address,
      abi: mcAbi,
      functionName: "userInfo",
      args: [BigInt(masterChefs.shib.pools[0].pid), address as Address],
      chainId: 109
    }, {
      address: masterChefs.shib.address,
      abi: mcAbi,
      functionName: "userInfo",
      args: [BigInt(masterChefs.shib.pools[1].pid), address as Address],
      chainId: 109
    }, {
      address: masterChefs.shib.pools[1].tokenA.address,
      abi: tokenAbi,
      functionName: "balanceOf",
      args: [masterChefs.shib.pools[1].lpAddress],
      chainId: 109
    }, {
      address: masterChefs.shib.pools[1].tokenB?.address as Address,
      abi: tokenAbi,
      functionName: "balanceOf",
      args: [masterChefs.shib.pools[1].lpAddress],
      chainId: 109
    }, {
      address: "0x2761723006d3Eb0d90B19B75654DbE543dcd974f",
      abi: tokenAbi,
      functionName: "balanceOf",
      args: [address as Address],
      chainId: 109
    }, {
      address: "0x1b47A8ae20fC5DC87fe566E97E693Fa6c761C851",
      abi: tokenAbi,
      functionName: "balanceOf",
      args: [address as Address],
      chainId: 2000
    }, {
      address: masterChefs.doge.address,
      abi: mcAbi,
      functionName: "userInfo",
      args: [BigInt(masterChefs.doge.pools[0].pid), address as Address],
      chainId: 2000
    }, {
      address: masterChefs.doge.address,
      abi: mcAbi,
      functionName: "userInfo",
      args: [BigInt(masterChefs.doge.pools[1].pid), address as Address],
      chainId: 2000
    }, {
      address: masterChefs.doge.pools[1].tokenA.address,
      abi: tokenAbi,
      functionName: "balanceOf",
      args: [masterChefs.doge.pools[1].lpAddress],
      chainId: 2000
    }, {
      address: masterChefs.doge.pools[1].tokenB?.address as Address,
      abi: tokenAbi,
      args: [masterChefs.doge.pools[1].lpAddress],
      functionName: "balanceOf",
      chainId: 2000
    }, {
      address: masterChefs.shib.pools[1].lpAddress,
      abi: tokenAbi,
      functionName: "totalSupply",
      chainId: 109
    }, {
      address: masterChefs.doge.pools[1].lpAddress,
      abi: tokenAbi,
      functionName: "totalSupply",
      chainId: 2000
    }]
  });
  useEffect(() => {
    if (balanceError) return;
    if (balanceLoading) return;
    if (balanceResults) {
      const shibBalance = balanceResults[4]?.status === "success" ? balanceResults[4].result : 0n;
      const mcBalance = balanceResults[0]?.status === "success" ? balanceResults[0].result[0] : 0n;
      const dogeMcBalance = balanceResults[6]?.status === "success" ? balanceResults[6].result[0] : 0n;
      const dogeBalance = balanceResults[5]?.status === "success" ? balanceResults[5].result : 0n;
      const shibLpBalance = balanceResults[1]?.status === "success" ? balanceResults[1].result[0] : 0n;
      const dogeLpBalance = balanceResults[7]?.status === "success" ? balanceResults[7].result[0] : 0n;
      const shibLpTokenA = balanceResults[2]?.status === "success" ? balanceResults[2].result : 0n;
      const shibLpTokenB = balanceResults[3]?.status === "success" ? balanceResults[3].result : 0n;
      const dogeLpTokenA = balanceResults[8]?.status === "success" ? balanceResults[8].result : 0n;
      const dogeLpTokenB = balanceResults[9]?.status === "success" ? balanceResults[9].result : 0n;
      const shibLpTotalSupply = balanceResults[10]?.status === "success" ? balanceResults[10].result : 0n;
      const dogeLpTotalSupply = balanceResults[11]?.status === "success" ? balanceResults[11].result : 0n;
      const shibLpShare = shibLpTotalSupply > 0n ? Number(formatEther(shibLpBalance)) / Number(formatEther(shibLpTotalSupply)) : 0;
      const dogeLpShare = dogeLpTotalSupply > 0n ? Number(formatEther(dogeLpBalance)) / Number(formatEther(dogeLpTotalSupply)) : 0;
      const shibLpChewyAmount = shibLpShare * Number(formatEther(shibLpTokenA));
      const dogeLpChewyAmount = dogeLpShare * Number(formatEther(dogeLpTokenA));
      console.log("Shib Balance: ", formatEther(shibBalance));
      console.log("MC Balance: ", formatEther(mcBalance));
      console.log("DC Balance: ", formatEther(dogeBalance));
      console.log("Doge MC Balance: ", formatEther(dogeMcBalance));
      console.log("Shib LP Balance: ", shibLpChewyAmount);
      console.log("Doge LP Balance: ", dogeLpChewyAmount);
      const total = Number(formatEther(shibBalance)) + Number(formatEther(mcBalance)) + Number(formatEther(dogeBalance)) + Number(shibLpChewyAmount ?? 0) + Number(dogeLpChewyAmount ?? 0);
      console.log("Total Balance: ", total);
      setBalance({
        shibarium: Number(formatEther(shibBalance) ?? 0),
        doge: Number(formatEther(dogeBalance) ?? 0),
        shibariumLp: Number(shibLpChewyAmount ?? 0),
        dogeLp: Number(dogeLpChewyAmount ?? 0),
        total: total
      });
      if (total >= minChewyPremium) {
        setIsPremium(true);
      } else if (total < minChewyPremium) {
        setPremPercentage(total / minChewyPremium * 100);
      } else {
        setIsPremium(false);
        setPremPercentage(0);
      }
    }
  }, [balanceResults, balanceLoading, balanceError]);
  return {
    balance,
    isPremium,
    premPercentage
  };
}